import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { switchMap } from 'rxjs/operators';
import {
  AuthService,
  CaseService,
  AppFunctionService,
  CareplanService,
  ErrorService,
  LanguageService,
  NotificationService,
  QuizResultsService,
} from '@/services';
import {
  TaskWorkgroup,
  TaskWorkgroupRegistration,
  TaskWorkgroupFunctionRegistration,
  TaskWorkgroupState,
  CaseRatingRegistration,
  MenuItem,
  QuizResultRegistration,
  User,
} from '@/models';
import { isCareplanNnnFunction } from '../../../utils/is-careplan-nnn-function';
import { TaskRegistrationDataComponent } from '../task-registration-data/task-registration-data.component';
import { TaskComponent } from '../task.component';
import { DialogDataModel } from '../types';

@Component({
  selector: 'app-task-teacher',
  templateUrl: './task-teacher.component.html',
  styleUrls: ['./task-teacher.component.scss'],
  standalone: false,
})
export class TaskTeacherComponent implements OnInit {
  @Input() data: DialogDataModel;
  @Input() dialogRef: MatDialogRef<TaskComponent>;

  selectedRegistration: TaskWorkgroup;
  selectedRegistrationId = '';

  workgroupRegistrationMenuItems: MenuItem[] = [];
  studentRegistrationMenuItems: MenuItem[] = [];

  private taskWorkgroupLookup: Map<string, TaskWorkgroup> = new Map();

  selectedWorkgroupRegistration: TaskWorkgroupRegistration | null = null;

  selectedWorkgroupRegistrationFormControl = new UntypedFormControl();
  selectedWorkgroupFunctionRegistrationFormControl = new UntypedFormControl();
  selectedRatingFormControl = new UntypedFormControl();
  newRatingCommentsFormControl = new UntypedFormControl();
  selectedTaskStateFormControl = new UntypedFormControl();

  editRating = false;
  ratingLoading = false;
  bulkRating = false;
  mobileTab: 'list' | 'submission' | 'rating' = 'list';

  /** Map tab name to mat-tab-group index */
  readonly mobileTabIndex: Record<'list' | 'submission' | 'rating', number> = {
    list: 0,
    submission: 1,
    rating: 2,
  };

  quizResult: QuizResultRegistration;
  dataRegistration: { dataRegistration: unknown; form: unknown } | null = null;
  functionType: string;
  taskStates: TaskWorkgroupState[];

  loading = false;
  previewNotAvailable = false;
  loadingCaseTaskWorkgroups = false;

  /** Flat array passed to the bulk rating child component */
  bulkWorkgroups: TaskWorkgroup[] = [];

  constructor(
    private authService: AuthService,
    private caseService: CaseService,
    private appFunctionService: AppFunctionService,
    private careplanService: CareplanService,
    private changeDectectorRef: ChangeDetectorRef,
    private notificationService: NotificationService,
    private quizResultsService: QuizResultsService,
    private errorService: ErrorService,
    private languageService: LanguageService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (this.data.task.functionToSubmit) {
      // Pre-select the function if specified on the task
    }

    this.loadingCaseTaskWorkgroups = true;

    this.caseService.getCaseTaskWorkgroupsForTaskId(this.data.task.id).subscribe({
      next: resp => {
        this.data.task.registrations = resp;
        if (this.data.task.registrations) {
          this.taskWorkgroupLookup.clear();
          this.workgroupRegistrationMenuItems = [];
          this.studentRegistrationMenuItems = [];

          for (const reg of this.data.task.registrations) {
            this.taskWorkgroupLookup.set(reg.id, reg);
            if (reg.workgroup.isStudent) {
              this.studentRegistrationMenuItems.push(this.toMenuItem(reg));
            } else {
              reg.workgroup.students.sort((a, b) => a.firstname.localeCompare(b.firstname));
              this.workgroupRegistrationMenuItems.push(this.toMenuItem(reg));
            }
          }
        }
        this.studentRegistrationMenuItems.sort((a, b) => a.name.localeCompare(b.name));
        this.workgroupRegistrationMenuItems.sort((a, b) => a.name.localeCompare(b.name));
        this.loadingCaseTaskWorkgroups = false;

        this.caseService.getTaskStates(this.data.task.id).subscribe({
          next: states => {
            this.taskStates = states;
          },
          error: err => this.errorService.onError(err),
        });

        const toOpenItem = this.studentRegistrationMenuItems.find(x => x.id === this.data.toOpenWorkgroup);
        if (toOpenItem) {
          this.setSelectedRegistration(toOpenItem.id);
        }
      },
      error: err => this.errorService.onError(err),
    });
  }

  toMenuItem(item: TaskWorkgroup): MenuItem {
    let name = '';
    const children: string[] = [];
    if (item.workgroup.isStudent) {
      name = `${item.workgroup.students[0].firstname} ${item.workgroup.students[0].lastname}`;
    } else {
      name = item.workgroup.name;
      children.push(...item.workgroup.students.map(s => `${s.firstname} ${s.lastname}`));
    }
    return { id: item.id, name, label: item.state.name, children };
  }

  setSelectedRegistration(registrationId?: string): void {
    if (!registrationId || registrationId === this.selectedRegistrationId) return;
    this.loading = true;

    this.caseService.getTaskWorkGroupForId(registrationId).subscribe({
      next: casetaskWorkgroup => {
        this.loading = false;
        this.dataRegistration = null;
        this.selectedRegistration = casetaskWorkgroup;
        this.selectedRegistrationId = casetaskWorkgroup.id;
        this.selectedWorkgroupRegistration = null;
        this.selectedWorkgroupRegistrationFormControl.reset();
        this.selectedTaskStateFormControl.reset();
        this.mobileTab = 'submission';
        if (casetaskWorkgroup.state) {
          this.selectedTaskStateFormControl.setValue(casetaskWorkgroup.state.name);
        }
        if (this.data.task.isQuiz) {
          this.quizResultsService
            .getResultsByCaseIdAndWorkgroupId(this.data.caseId, casetaskWorkgroup.workgroup.id)
            .subscribe({
              next: resp => {
                this.quizResult = resp as QuizResultRegistration;
              },
              error: err => this.errorService.onError(err),
            });
          return;
        }
        if (casetaskWorkgroup.registrations && casetaskWorkgroup.registrations.length === 1) {
          this.onRegistrationChange({ value: casetaskWorkgroup.registrations[0] });
        }
      },
      error: err => {
        this.errorService.onError(err);
        this.selectedRegistrationId = '';
      },
    });
  }

  submitRating(): void {
    if (!this.selectedRatingFormControl.value) return;

    this.ratingLoading = true;
    this.editRating = false;

    const caseRatingRegistration = new CaseRatingRegistration();
    caseRatingRegistration.caseRating = this.selectedRatingFormControl.value;
    caseRatingRegistration.comments = this.newRatingCommentsFormControl.value;

    if (this.selectedWorkgroupRegistration) {
      this.caseService
        .submitTaksRegistrationRating(
          this.data.caseId,
          this.data.task.id,
          this.selectedWorkgroupRegistration.id,
          caseRatingRegistration
        )
        .subscribe({
          next: resp => {
            this.ratingLoading = false;
            this.selectedWorkgroupRegistration = resp as TaskWorkgroupRegistration;
            this.dataRegistration = null;
            this.notificationService.success(this.languageService.translateWithCap('case.task.rating-saved'));
          },
          error: err => this.errorService.onError(err),
        });
    } else {
      const reg = new TaskWorkgroupRegistration();
      reg.done = false;
      reg.submittedBy = new User();
      reg.submittedBy.id = this.authService.getLoggedInUserId();
      reg.functionRegistrations = [];

      this.caseService
        .submitTask(this.data.caseId, this.data.task.id, this.selectedRegistration.workgroup.id, reg)
        .pipe(
          switchMap(resp => {
            const wg = resp as TaskWorkgroup;
            const regId = wg.registrations[0]?.id;
            return this.caseService.submitTaksRegistrationRating(
              this.data.caseId,
              this.data.task.id,
              regId,
              caseRatingRegistration
            );
          })
        )
        .subscribe({
          next: resp => {
            this.ratingLoading = false;
            this.selectedWorkgroupRegistration = resp as TaskWorkgroupRegistration;
            this.selectedRegistration.registrations.push(this.selectedWorkgroupRegistration);
            this.notificationService.success(this.languageService.translateWithCap('case.task.rating-saved'));
          },
          error: err => this.errorService.onError(err),
        });
    }
  }

  saveState(): void {
    const tws = new TaskWorkgroupState();
    tws.name = this.selectedTaskStateFormControl.value;
    this.caseService
      .updateTaskWorkgroupState(this.data.caseId, this.data.task.id, this.selectedRegistration.workgroup.id, tws)
      .subscribe({
        next: resp => {
          this.selectedRegistration.state = (resp as TaskWorkgroup).state;
          this.taskWorkgroupLookup.set(this.selectedRegistration.id, this.selectedRegistration);
          const name = this.selectedRegistration.workgroup.isStudent
            ? `${this.selectedRegistration.workgroup.students[0].firstname} ${this.selectedRegistration.workgroup.students[0].lastname}`
            : this.selectedRegistration.workgroup.name;
          this.notificationService.success(
            this.languageService.translateWithCap('case.tasks.notifications.status-update', {
              USER: name,
              STATE: this.caseService.getStateTextAndColor(this.selectedRegistration.state.name, false).text,
            })
          );
          this.changeDectectorRef.detectChanges();
        },
        error: err => this.errorService.onError(err),
      });
  }

  onRegistrationChange(event: { value: TaskWorkgroupRegistration }): void {
    this.dataRegistration = null;
    this.selectedWorkgroupFunctionRegistrationFormControl.reset();
    this.selectedWorkgroupRegistration = event.value;
    if (
      this.selectedWorkgroupRegistration.functionRegistrations &&
      this.selectedWorkgroupRegistration.functionRegistrations.length === 1
    ) {
      this.setTeacherFunctionRegistrationData(this.selectedWorkgroupRegistration.functionRegistrations[0]);
    }
  }

  onWorkgroupFunctionRegistrationChange(event: MatSelectChange): void {
    this.setTeacherFunctionRegistrationData(event.value as TaskWorkgroupFunctionRegistration);
  }

  private setTeacherFunctionRegistrationData(funcReg: TaskWorkgroupFunctionRegistration): void {
    this.previewNotAvailable = false;
    this.loading = true;
    if (isCareplanNnnFunction(funcReg.function)) {
      this.functionType = 'careplan-nnn';
      this.dataRegistration = { dataRegistration: { id: funcReg.functionRegistrationId }, form: funcReg.function };
      this.loading = false;
      return;
    }
    if (funcReg.function.type === 'careplan') {
      this.functionType = 'careplan';
      this.dataRegistration = null;
      this.careplanService.getCareplanRegistrationById(funcReg.functionRegistrationId, this.data.patientId).subscribe({
        next: resp => {
          this.loading = false;
          this.dataRegistration = { dataRegistration: resp, form: funcReg.function };
        },
        error: err => this.errorService.onError(err),
      });
      return;
    }
    this.dataRegistration = null;
    this.functionType = funcReg.function.type === 'formGroupForm' ? 'formGroupForm' : 'anamnesisForm';
    this.appFunctionService
      .getFunctionRegistrationData(
        funcReg.function.backendEndpoint.replace('{id}', funcReg.function.id) + '/' + funcReg.functionRegistrationId
      )
      .subscribe({
        next: resp => {
          this.loading = false;
          this.dataRegistration = { dataRegistration: resp, form: funcReg.function };
        },
        error: err => this.errorService.onError(err),
      });
  }

  getTaskStateName(state: TaskWorkgroupState): string {
    return this.caseService.getStateTextAndColor(state.name, false).text;
  }

  checkIfQuizResultPassed(): boolean {
    const passingScore = +this.quizResult.passingScore;
    const score = +this.quizResult.score;
    return score >= passingScore;
  }

  onMobileTabChange(index: number): void {
    const tabs: Array<'list' | 'submission' | 'rating'> = ['list', 'submission', 'rating'];
    this.mobileTab = tabs[index] ?? 'list';
  }

  // ----- Bulk rating -----

  setBulkData(): void {
    this.bulkWorkgroups = [
      ...this.workgroupRegistrationMenuItems.map(item => this.taskWorkgroupLookup.get(item.id)!),
      ...this.studentRegistrationMenuItems.map(item => this.taskWorkgroupLookup.get(item.id)!),
    ].filter(Boolean);
  }

  openFunctionRegistrationDataDialog(funcReg: TaskWorkgroupFunctionRegistration): void {
    if (isCareplanNnnFunction(funcReg.function)) {
      this.dialog.open(TaskRegistrationDataComponent, {
        maxWidth: '95vw',
        width: '100%',
        maxHeight: '75vh',
        data: {
          careInstitutionId: this.data.careInstitutionId,
          patientId: this.data.patientId,
          dataRegistration: { id: funcReg.functionRegistrationId },
          form: funcReg.function,
          functionType: 'careplan-nnn',
        },
      });
      return;
    }
    if (funcReg.function.type === 'careplan') {
      this.careplanService
        .getCareplanRegistrationById(funcReg.functionRegistrationId, this.data.patientId)
        .pipe(
          switchMap(cp =>
            this.careplanService
              .getCareplanRegistrationRows(this.data.patientId, 'FINAL' as any, funcReg.functionRegistrationId)
              .pipe(
                switchMap(rows => {
                  (cp as any).registrationRows = rows;
                  this.dialog.open(TaskRegistrationDataComponent, {
                    maxWidth: '95vw',
                    width: '100%',
                    maxHeight: '75vh',
                    data: {
                      careInstitutionId: this.data.careInstitutionId,
                      dataRegistration: cp,
                      form: funcReg.function,
                      functionType: 'careplan',
                    },
                  });
                  return [];
                })
              )
          )
        )
        .subscribe({ error: err => this.errorService.onError(err) });
      return;
    }
    this.appFunctionService
      .getFunctionRegistrationData(
        funcReg.function.backendEndpoint.replace('{id}', funcReg.function.id) + '/' + funcReg.functionRegistrationId
      )
      .subscribe({
        next: resp => {
          this.dialog.open(TaskRegistrationDataComponent, {
            maxWidth: '65vw',
            width: '100%',
            maxHeight: '75vh',
            data: { dataRegistration: resp, form: funcReg.function, functionType: 'form' },
          });
        },
        error: err => this.errorService.onError(err),
      });
  }
}
